import { useState, useEffect, FormEvent } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { Alert, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import useApi from '../../hooks/useApi'
import Button from '../button'
import './StripeSetupForm.scss'

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
const stripePromise = loadStripe(stripeKey)

interface SetupFormProps {
  onSuccess: (paymentMethodId: string) => void
  onError: (error: string) => void
  onCancel?: () => void
}

function SetupForm({ onSuccess, onError, onCancel }: SetupFormProps) {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      })

      if (stripeError) {
        const errorMsg = stripeError.message || 'Payment setup failed'
        setError(errorMsg)
        onError(errorMsg)
      } else if (setupIntent?.status === 'succeeded') {
        // Payment method ID is in setupIntent.payment_method
        const paymentMethodId = setupIntent.payment_method as string
        onSuccess(paymentMethodId)
      } else {
        const errorMsg = 'Payment setup was not successful'
        setError(errorMsg)
        onError(errorMsg)
      }
    } catch (err: any) {
      const errorMsg =
        err?.message || 'An error occurred during payment setup'
      setError(errorMsg)
      onError(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="stripe-setup-form">
      <div className="form-group mb-3">
        <label className="form-label fw-semibold">
          {t('sponsorship.pledgeModal.paymentStep.title')}
        </label>
        <p className="text-muted mb-3">
          {t('sponsorship.pledgeModal.paymentStep.description')}
        </p>
        <div className="payment-element-wrapper">
          <PaymentElement
            options={{
              layout: 'tabs',
            }}
          />
        </div>
        <small className="text-muted d-block mt-2">
          {t('sponsorship.pledgeModal.paymentStep.achDescription')}
        </small>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="d-flex gap-2 justify-content-end">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            {t('buttons.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={!stripe || loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {t('sponsorship.pledgeModal.paymentStep.processing')}
            </>
          ) : (
            t('sponsorship.pledgeModal.paymentStep.submitButton')
          )}
        </Button>
      </div>

      <div className="mt-3 text-center">
        <small className="text-muted">
          Test card: 4242 4242 4242 4242 | Any future date | Any CVC
        </small>
      </div>
    </form>
  )
}

interface StripeSetupFormProps {
  onSuccess: (paymentMethodId: string) => void
  onError?: (error: string) => void
  onCancel?: () => void
}

export default function StripeSetupForm({
  onSuccess,
  onError,
  onCancel,
}: StripeSetupFormProps) {
  const { t } = useTranslation()
  const { createPledgeSetupIntent } = useApi()

  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch SetupIntent client_secret on mount
  useEffect(() => {
    const fetchSetupIntent = async () => {
      try {
        const response: any = await createPledgeSetupIntent()

        if (response?.success === false) {
          const errMsg =
            response?.error?.response?.data?.error ||
            response?.error?.message ||
            'Failed to initialize payment setup'
          throw new Error(errMsg)
        }

        const data = response?.data || response
        if (!data?.client_secret) {
          throw new Error('Failed to initialize payment setup')
        }

        setClientSecret(data.client_secret)
      } catch (err: any) {
        const errorMsg = err?.message || 'Failed to initialize payment setup'
        setError(errorMsg)
        onError?.(errorMsg)
      } finally {
        setLoading(false)
      }
    }

    fetchSetupIntent()
  }, [])

  const handleError = (errorMsg: string) => {
    setError(errorMsg)
    onError?.(errorMsg)
  }

  if (loading) {
    return (
      <div className="stripe-setup-loading text-center py-4">
        <Spinner animation="border" className="mb-2" />
        <p>{t('sponsorship.pledgeModal.paymentStep.loading')}</p>
      </div>
    )
  }

  if (error && !clientSecret) {
    return (
      <div className="stripe-setup-error">
        <Alert variant="danger">{error}</Alert>
        {onCancel && (
          <Button variant="secondary" onClick={onCancel}>
            {t('buttons.back')}
          </Button>
        )}
      </div>
    )
  }

  if (!clientSecret) {
    return null
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#E6526C',
          },
        },
      }}
    >
      <SetupForm
        onSuccess={onSuccess}
        onError={handleError}
        onCancel={onCancel}
      />
    </Elements>
  )
}
